import type { Course, CourseHole } from '@/lib/db/types';

export type CourseCampus = 'Arlington' | 'Fairfax' | 'Other';

const COMBO_LABELS: Record<string, string> = {
  'arl-br': 'Blue / Red',
  'arl-rw': 'Red / White',
  'arl-wb': 'White / Blue',
  'ffx-br': 'Blue / Red',
  'ffx-rw': 'Red / White',
  'ffx-wb': 'White / Blue',
};

export const COMBO_SLUG_ORDER = [
  'arl-br', 'arl-rw', 'arl-wb',
  'ffx-br', 'ffx-rw', 'ffx-wb',
];

export function getCourseCampus(course: Course): CourseCampus {
  const text = `${course.slug} ${course.name}`.toLowerCase();
  if (text.includes('arlington') || text.includes('arl-') || text.includes('arl ')) return 'Arlington';
  if (text.includes('fairfax') || text.includes('ffx-') || text.includes('ffx ')) return 'Fairfax';
  return 'Other';
}

export function getCourseComboLabel(course: Pick<Course, 'slug' | 'name'>): string {
  if (COMBO_LABELS[course.slug]) return COMBO_LABELS[course.slug];
  const parts = course.name.split(/\s*[—–-]\s*/);
  if (parts.length >= 2) return parts[parts.length - 1].trim();
  return course.name;
}

export function getCourseDisplayTitle(course: Pick<Course, 'name'>): string {
  return course.name;
}

export function courseHandicap(
  index: number | null | undefined,
  slope: number | null | undefined,
): number {
  if (index == null || slope == null) return 0;
  return Math.round(index * slope / 113);
}

export function sortCoursesByCombo(courses: Course[]): Course[] {
  return [...courses].sort((a, b) => {
    const ai = COMBO_SLUG_ORDER.indexOf(a.slug);
    const bi = COMBO_SLUG_ORDER.indexOf(b.slug);
    const aRank = ai >= 0 ? ai : 999;
    const bRank = bi >= 0 ? bi : 999;
    if (aRank !== bRank) return aRank - bRank;
    return a.name.localeCompare(b.name);
  });
}

/** Distribute course handicap strokes by stroke index (prototype parity). */
export function strokesPerHole(courseHcp: number, holes: CourseHole[]): number[] {
  const sorted = [...holes].sort((a, b) => a.hole_num - b.hole_num);
  const arr = sorted.map(() => 0);
  if (!courseHcp || courseHcp <= 0) return arr;
  let remaining = courseHcp;
  let pass = 1;
  while (remaining > 0) {
    const targetSi = ((pass - 1) % 18) + 1;
    const idx = sorted.findIndex((h) => h.stroke_index === targetSi);
    if (idx >= 0) arr[idx] += 1;
    remaining -= 1;
    pass += 1;
  }
  return arr;
}

export function scoreToParLabel(gross: number, par: number): string {
  const d = gross - par;
  if (d === 0) return 'E';
  return d > 0 ? `+${d}` : `${d}`;
}

export function groupCoursesByCampus(courses: Course[]): Record<CourseCampus, Course[]> {
  const groups: Record<CourseCampus, Course[]> = { Arlington: [], Fairfax: [], Other: [] };
  for (const c of courses) {
    groups[getCourseCampus(c)].push(c);
  }
  for (const campus of CAMPUS_ORDER) {
    groups[campus] = sortCoursesByCombo(groups[campus]);
  }
  return groups;
}

export const CAMPUS_ORDER: CourseCampus[] = ['Arlington', 'Fairfax', 'Other'];
